import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/api";
import { toast } from "sonner";
import { Copy, ExternalLink, LogOut, Plus } from "lucide-react";
import { useLang } from "@/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import { registry as themeRegistry } from "@/themes/registry";

const themeIds = Object.keys(themeRegistry);
const defaultThemeId = themeRegistry["blossom-gold"] ? "blossom-gold" : (themeIds[0] || "blossom-gold");

const emptyForm = {
    slug: "",
    groom_en: "",
    bride_en: "",
    groom_ar: "",
    bride_ar: "",
    date_iso: "",
    end_iso: "",
    venue_en: "",
    venue_ar: "",
    city_en: "",
    city_ar: "",
    hall_en: "",
    hall_ar: "",
    theme_id: defaultThemeId,
    drive_folder_id: "",
    admin_email: "",
    admin_password: "",
};

function isoFromLocal(value) {
    if (!value) return "";
    if (/(Z|[+-]\d{2}:\d{2})$/.test(value)) return value;
    return `${value}:00+03:00`;
}

async function copyText(text) {
    try {
        if (navigator.clipboard?.writeText && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {}

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-1000px";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
        return document.execCommand("copy");
    } catch {
        return false;
    } finally {
        document.body.removeChild(textarea);
    }
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</span>
            {children}
        </label>
    );
}

const inputClass = "mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:bg-white focus:border-slate-400";

export default function WeddingList() {
    const nav = useNavigate();
    const { t, dir, lang } = useLang();
    const [role, setRole] = useState("");
    const [weddings, setWeddings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(emptyForm);
    const [creating, setCreating] = useState(false);

    const load = useCallback(async () => {
        const [me, list] = await Promise.all([
            api.get("/auth/me"),
            api.get("/admin/weddings"),
        ]);
        setRole(me.data?.role || "");
        setWeddings(Array.isArray(list.data) ? list.data : []);
    }, []);

    useEffect(() => {
        let cancelled = false;
        load()
            .catch((e) => {
                if (cancelled) return;
                if (e?.response?.status === 401) {
                    nav("/admin/login");
                    return;
                }
                toast.error(e?.response?.data?.detail || t("adminError"));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [load, nav, t]);

    const logout = () => {
        localStorage.removeItem("wd-admin-token");
        nav("/admin/login");
    };

    const setField = (key, value) => {
        setForm((current) => ({ ...current, [key]: value }));
    };

    const copyOpenUrl = async (wedding) => {
        const url = wedding.open_url || `${window.location.origin}/w/${wedding.slug}/i/invitation`;
        const copied = await copyText(url);
        if (copied) {
            toast.success(t("adminInvitationCopied"));
        } else {
            window.prompt(t("adminCopyPrompt"), url);
        }
    };

    const createWedding = async (event) => {
        event.preventDefault();
        setCreating(true);
        try {
            const adminEmail = form.admin_email.trim().toLowerCase();
            const adminPassword = form.admin_password;
            const hasAdminEmail = adminEmail.length > 0;
            const hasAdminPassword = adminPassword.length > 0;
            if (hasAdminEmail !== hasAdminPassword) {
                toast.error(t("adminWeddingAdminBothRequired"));
                return;
            }
            const payload = {
                slug: form.slug.trim(),
                groom_en: form.groom_en.trim(),
                bride_en: form.bride_en.trim(),
                groom_ar: form.groom_ar.trim(),
                bride_ar: form.bride_ar.trim(),
                date_iso: isoFromLocal(form.date_iso),
                end_iso: isoFromLocal(form.end_iso),
                venue_en: form.venue_en.trim(),
                venue_ar: form.venue_ar.trim(),
                city_en: form.city_en.trim(),
                city_ar: form.city_ar.trim(),
                hall_en: form.hall_en.trim(),
                hall_ar: form.hall_ar.trim(),
                theme_id: form.theme_id || defaultThemeId,
                drive_folder_id: form.drive_folder_id.trim(),
            };
            if (hasAdminEmail) {
                payload.admin_email = adminEmail;
                payload.admin_password = adminPassword;
            }
            await api.post("/admin/weddings", payload);
            toast.success(hasAdminEmail ? t("adminWeddingAdminCreated") : t("adminWeddingCreated"));
            setForm(emptyForm);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("adminError"));
        } finally {
            setCreating(false);
        }
    };

    const isPlatformAdmin = role === "platform_admin";

    return (
        <div className="admin-scope min-h-[100dvh] bg-slate-50" dir={dir}>
            <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                        <h1 className="text-lg font-semibold text-slate-900">{t("adminWeddingsTitle")}</h1>
                        <p className="text-xs text-slate-500">{t("adminWeddingsHint")}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <LanguageToggle />
                        <button onClick={logout} className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5" data-testid="admin-logout-btn">
                            <LogOut size={15} /> {t("logout")}
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 md:px-6 py-8 space-y-8">
                {isPlatformAdmin && (
                    <form onSubmit={createWedding} className="bg-white border border-slate-200 rounded-xl p-4 md:p-5 space-y-4" data-testid="admin-create-wedding-form">
                        <h2 className="text-base font-semibold text-slate-900">{t("adminCreateWedding")}</h2>
                        <div className="grid gap-4 md:grid-cols-2">
                            <Field label={t("adminWeddingSlug")}>
                                <input
                                    required
                                    dir="ltr"
                                    value={form.slug}
                                    onChange={(e) => setField("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 40))}
                                    pattern="[a-z0-9-]{2,40}"
                                    className={inputClass}
                                    data-testid="admin-create-wedding-slug"
                                />
                                <span className="mt-1 block text-[11px] text-slate-400">{t("adminSlugHint")}</span>
                            </Field>
                            <Field label={t("adminDriveFolder")}>
                                <input
                                    required
                                    dir="ltr"
                                    value={form.drive_folder_id}
                                    onChange={(e) => setField("drive_folder_id", e.target.value)}
                                    className={inputClass}
                                    data-testid="admin-create-wedding-drive-folder"
                                />
                            </Field>
                            <Field label={t("adminGroomEn")}>
                                <input required value={form.groom_en} onChange={(e) => setField("groom_en", e.target.value)} className={inputClass} data-testid="admin-create-wedding-groom-en" />
                            </Field>
                            <Field label={t("adminBrideEn")}>
                                <input required value={form.bride_en} onChange={(e) => setField("bride_en", e.target.value)} className={inputClass} data-testid="admin-create-wedding-bride-en" />
                            </Field>
                            <Field label={t("adminGroomAr")}>
                                <input required dir="rtl" value={form.groom_ar} onChange={(e) => setField("groom_ar", e.target.value)} className={inputClass} data-testid="admin-create-wedding-groom-ar" />
                            </Field>
                            <Field label={t("adminBrideAr")}>
                                <input required dir="rtl" value={form.bride_ar} onChange={(e) => setField("bride_ar", e.target.value)} className={inputClass} data-testid="admin-create-wedding-bride-ar" />
                            </Field>
                            <Field label={t("adminStartDate")}>
                                <input required type="datetime-local" value={form.date_iso} onChange={(e) => setField("date_iso", e.target.value)} className={inputClass} data-testid="admin-create-wedding-date" />
                            </Field>
                            <Field label={t("adminEndDate")}>
                                <input required type="datetime-local" value={form.end_iso} onChange={(e) => setField("end_iso", e.target.value)} className={inputClass} data-testid="admin-create-wedding-end" />
                            </Field>
                            <Field label={t("adminVenueEn")}>
                                <input required value={form.venue_en} onChange={(e) => setField("venue_en", e.target.value)} className={inputClass} data-testid="admin-create-wedding-venue-en" />
                            </Field>
                            <Field label={t("adminVenueAr")}>
                                <input required dir="rtl" value={form.venue_ar} onChange={(e) => setField("venue_ar", e.target.value)} className={inputClass} data-testid="admin-create-wedding-venue-ar" />
                            </Field>
                            <Field label={t("adminCityEn")}>
                                <input required value={form.city_en} onChange={(e) => setField("city_en", e.target.value)} className={inputClass} data-testid="admin-create-wedding-city-en" />
                            </Field>
                            <Field label={t("adminCityAr")}>
                                <input required dir="rtl" value={form.city_ar} onChange={(e) => setField("city_ar", e.target.value)} className={inputClass} data-testid="admin-create-wedding-city-ar" />
                            </Field>
                            <Field label={t("adminHallEn")}>
                                <input required value={form.hall_en} onChange={(e) => setField("hall_en", e.target.value)} className={inputClass} data-testid="admin-create-wedding-hall-en" />
                            </Field>
                            <Field label={t("adminHallAr")}>
                                <input required dir="rtl" value={form.hall_ar} onChange={(e) => setField("hall_ar", e.target.value)} className={inputClass} data-testid="admin-create-wedding-hall-ar" />
                            </Field>
                            <Field label={t("adminTheme")}>
                                <select
                                    value={themeRegistry[form.theme_id] ? form.theme_id : defaultThemeId}
                                    onChange={(e) => setField("theme_id", e.target.value)}
                                    className={inputClass}
                                    data-testid="admin-create-wedding-theme"
                                >
                                    {themeIds.map((id) => (
                                        <option key={id} value={id}>{themeRegistry[id].name}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label={t("adminWeddingAdminEmail")}>
                                <input
                                    type="email"
                                    dir="ltr"
                                    autoComplete="off"
                                    value={form.admin_email}
                                    onChange={(e) => setField("admin_email", e.target.value)}
                                    className={inputClass}
                                    data-testid="admin-create-wedding-admin-email"
                                />
                            </Field>
                            <Field label={t("adminWeddingAdminPassword")}>
                                <input
                                    type="password"
                                    dir="ltr"
                                    autoComplete="new-password"
                                    value={form.admin_password}
                                    onChange={(e) => setField("admin_password", e.target.value)}
                                    className={inputClass}
                                    data-testid="admin-create-wedding-admin-password"
                                />
                                <span className="mt-1 block text-[11px] text-slate-400">{t("adminWeddingAdminHint")}</span>
                            </Field>
                        </div>
                        <button
                            type="submit"
                            disabled={creating}
                            className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:opacity-60"
                            data-testid="admin-create-wedding-submit"
                        >
                            <Plus size={16} /> {creating ? t("adminSaving") : t("adminCreateWeddingButton")}
                        </button>
                    </form>
                )}

                {loading ? null : (
                    <div className="space-y-3" data-testid="admin-wedding-list">
                        {weddings.length === 0 && (
                            <p className="text-sm text-slate-500" data-testid="admin-weddings-empty">{t("adminNoWeddings")}</p>
                        )}
                        {weddings.map((wedding) => {
                            const dateLabel = wedding.date_iso
                                ? new Date(wedding.date_iso).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US")
                                : t("adminDateNotSet");
                            return (
                                <article
                                    key={wedding.id || wedding.slug}
                                    className="bg-white border border-slate-200 rounded-xl p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4"
                                    data-testid={`admin-wedding-row-${wedding.slug}`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <h2 className="text-base font-semibold text-slate-900">
                                            {wedding.groom_en} & {wedding.bride_en}
                                        </h2>
                                        <p className="mt-1 text-sm text-slate-500">
                                            {dateLabel} · {wedding.theme_id}
                                        </p>
                                        <p className="mt-1 text-xs text-slate-400 truncate" dir="ltr" data-testid={`admin-wedding-path-${wedding.slug}`}>
                                            {wedding.public_path}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => copyOpenUrl(wedding)}
                                            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white text-slate-700 px-3 py-2 text-sm hover:bg-slate-100"
                                            data-testid={`admin-wedding-copy-${wedding.slug}`}
                                        >
                                            <Copy size={15} /> {t("adminCopyOpenLink")}
                                        </button>
                                        <Link
                                            to={`/w/${wedding.slug}/admin`}
                                            className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 text-white px-3 py-2 text-sm hover:bg-slate-800"
                                            data-testid={`admin-wedding-open-${wedding.slug}`}
                                        >
                                            <ExternalLink size={15} /> {t("adminOpenDashboard")}
                                        </Link>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </main>
        </div>
    );
}
